import { useState } from 'react';
import {
  Box, Grid, Stack, Typography, Paper, Chip, Alert, Card, CardActionArea,
  Radio, TextField, Collapse, IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { setEffectiveMode, setEffectiveDate } from '../../../../redux/pricingWizardSlice';
import { formatDate, daysFromToday, todayISO, tomorrowISO, SLAB_TEMPLATES, slabsForTemplate, summarizeSlabGroups, foldIndependentRvpIntoRows } from '../helpers/rateEngine';
import SectionTitle from '../components/SectionTitle';

const AUDIT_LABELS = {
  commercial_construct: 'Commercial construct',
  tcs_blended: 'Serviceability & guardrails',
  consolidated_review: 'Consolidated review · lock for commit',
  parent___blended_surface__: 'Blended rate card · Surface',
  parent___blended_air__: 'Blended rate card · Air',
  parent___blended_sdd_ndd__: 'Blended rate card · SDD / NDD',
};
function auditLabel(k) {
  if (AUDIT_LABELS[k]) return AUDIT_LABELS[k];
  if (k.startsWith('rvp_parent_')) return 'RVP rate card · ' + k.slice(11).replace(/_/g, ' ');
  if (k.startsWith('parent_')) return 'Courier rate card · ' + k.slice(7).replace(/_/g, ' ');
  return k;
}

export default function Stage6_Review() {
  const dispatch = useDispatch();
  const s = useSelector((st) => st.pricingWizard);
  // Show exactly what save-rate-card will send: for independent (Mode B) RVP the
  // editable matrix is folded onto each row's rvp_qc/rvp_noqc at save time, so the
  // review must reflect that too. No-op for forward-linked (Mode A) / disabled RVP.
  const rows = foldIndependentRvpIntoRows(s.rateCard.rows, s);
  const accs = [...new Set(rows.map((r) => r.account_code))];
  const today = todayISO();
  const [covOpen, setCovOpen] = useState(false);
  const [previewAcc, setPreviewAcc] = useState(accs[0]);

  // Coverage map
  const coverByParent = {};
  rows.forEach((r) => {
    coverByParent[r.parent] = coverByParent[r.parent] || {};
    coverByParent[r.parent][r.account_code] = coverByParent[r.parent][r.account_code] || new Set();
    coverByParent[r.parent][r.account_code].add(r.service);
  });
  const parentRows = Object.keys(coverByParent).sort();

  const auditKeys = Object.keys(s.confirmations);
  const tpl = SLAB_TEMPLATES.find((t) => t.id === s.rateCard.slabTemplate);
  const slabGroups = summarizeSlabGroups(slabsForTemplate(s));

  const effChip = s.effective_mode === 'immediate'
    ? `📅 Effective today (${formatDate(today)})`
    : (s.effective_date ? `📅 Scheduled · ${formatDate(s.effective_date)} (${daysFromToday(s.effective_date)})` : '📅 Scheduled · date not set');

  const previewRows = rows.filter((r) => r.account_code === previewAcc).slice(0, 60);

  return (
    <Box>
      <Alert severity="info" icon={<span>📑</span>} sx={{ mb: 1.5 }}>
        Final review — signed agreement on the left, generated consolidated base file on the right. Pick when the rate card should go live, then click <b>Confirm & lock for commit</b> via Next.
      </Alert>

      {/* Toolbar chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip label={s.merchant?.brand || 'Merchant'} size="small" sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 700 }} />
        <Chip label={`${s.resolvedServices.length} services`} size="small" color="primary" variant="outlined" />
        <Chip label={`${accs.length} account codes`} size="small" color="success" variant="outlined" />
        <Chip label={`${rows.length} rate rows`} size="small" variant="outlined" />
        <Chip label={`GST ${s.agreement.gst_stance}`} size="small" color={s.agreement.gst_stance === 'inclusive' ? 'warning' : 'info'} variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Chip label={effChip} size="small" color={s.effective_mode === 'scheduled' && !s.effective_date ? 'warning' : 'info'} />
      </Stack>

      {/* Audit trail */}
      {auditKeys.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <SectionTitle icon="✓" title="Audit trail" badge={`${auditKeys.length} confirmation${auditKeys.length > 1 ? 's' : ''}`} />
          <Table size="small" sx={{ mt: 0.5 }}>
            <TableBody>
              {auditKeys.map((k) => {
                const c = s.confirmations[k];
                const dt = new Date(c.at).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                return (
                  <TableRow key={k}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, border: 0 }}>{auditLabel(k)}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', border: 0 }}>Confirmed by <b>{c.by}</b> on {dt}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Slab summary */}
      <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderColor: 'primary.light' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>⚖ Slab template</Typography>
        <Chip size="small" label={tpl?.label} sx={{ bgcolor: 'background.neutral', fontWeight: 600 }} />
        {slabGroups.map((g, i) => <Chip key={i} size="small" label={`${g.count} × ${g.width} GMS`} sx={{ bgcolor: 'primary.lighter', color: 'primary.dark', fontWeight: 700, fontSize: 10.5 }} />)}
      </Paper>

      {/* Courier coverage accordion */}
      <Paper variant="outlined" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, cursor: 'pointer' }} onClick={() => setCovOpen((v) => !v)}>
          <IconButton size="small" sx={{ p: 0.25, transform: covOpen ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}><KeyboardArrowRightIcon fontSize="small" /></IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>🗺 Courier coverage map</Typography>
          <Chip label={`${parentRows.length} parents`} size="small" color="primary" variant="outlined" />
          <Chip label={`${accs.length} codes`} size="small" sx={{ bgcolor: '#ede9fe', color: '#5b21b6' }} />
        </Stack>
        <Collapse in={covOpen}>
          <TableContainer sx={{ borderTop: 1, borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', whiteSpace: 'nowrap' } }}>
                  <TableCell sx={{ width: 160 }}>Parent courier</TableCell>
                  <TableCell sx={{ width: 200 }}>Account code</TableCell>
                  <TableCell>Service types covered</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parentRows.map((par) => {
                  const codes = Object.keys(coverByParent[par]).sort();
                  return codes.map((c, ci) => {
                    const status = s.rateCard.completion[c] || 'in-progress';
                    const isConfirmed = status === 'complete';
                    return (
                      <TableRow key={`${par}-${c}`} hover sx={{ '& td': { fontSize: 12, py: 0.75 } }}>
                        {/* Parent — only shown on first code row, rowSpan covers rest */}
                        {ci === 0 && (
                          <TableCell
                            rowSpan={codes.length}
                            sx={{ fontWeight: 700, verticalAlign: 'top', borderRight: (t) => `1px solid ${t.palette.divider}`, bgcolor: 'background.neutral', fontSize: 12 }}
                          >
                            {par}
                            <Chip label={`${codes.length} code${codes.length > 1 ? 's' : ''}`} size="small" sx={{ display: 'block', mt: 0.5, height: 16, fontSize: 9.5, width: 'fit-content' }} />
                          </TableCell>
                        )}
                        <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{c}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.4}>
                            {[...coverByParent[par][c]].map((svc) => (
                              <Chip key={svc} label={svc} size="small" sx={{ fontSize: 9.5, height: 18, bgcolor: 'primary.lighter', color: 'primary.dark' }} />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={isConfirmed ? '✓ Confirmed' : '⚠ Pending'}
                            size="small"
                            color={isConfirmed ? 'success' : 'warning'}
                            variant="outlined"
                            sx={{ fontSize: 10.5, fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Paper>

      {/* Schedule card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'primary.light' }}>
        <SectionTitle icon="📅" title="When should this rate card take effect?" badge="required" />
        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
          {[
            { id: 'immediate', title: 'Effective immediately', sub: `Goes live today (${formatDate(today)}). Shipments booked from now use this rate card.` },
            { id: 'from_date', title: 'Schedule for a future date', sub: 'Lock the rate card now but defer go-live (e.g. start of next billing cycle).' },
          ].map((o) => (
            <Grid item xs={12} md={6} key={o.id}>
              <Card variant="outlined" sx={{ height: '100%', borderColor: s.effective_mode === o.id ? 'primary.main' : 'divider', bgcolor: s.effective_mode === o.id ? 'primary.lighter' : 'background.paper' }}>
                <CardActionArea sx={{ p: 1.5, height: '100%' }} onClick={() => { dispatch(setEffectiveMode(o.id)); dispatch(setEffectiveDate(o.id === 'immediate' ? today : '')); }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Radio checked={s.effective_mode === o.id} sx={{ p: 0.25, pointerEvents: 'none' }} />
                    <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{o.title}</Typography><Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.sub}</Typography></Box>
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        {s.effective_mode === 'from_date' && (
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" type="date" label="Go-live date *" InputLabelProps={{ shrink: true }} inputProps={{ min: tomorrowISO() }}
                value={s.effective_date || ''} onChange={(e) => dispatch(setEffectiveDate(e.target.value))} helperText="Pick any date from tomorrow onwards. Until then, old card stays active." />
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert severity={s.effective_date && s.effective_date > today ? 'success' : 'warning'} sx={{ m: 0 }}>
                {s.effective_date && s.effective_date > today ? <>Will go live on <b>{formatDate(s.effective_date)}</b> ({daysFromToday(s.effective_date)})</> : 'Pick a date from tomorrow onwards'}
              </Alert>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Side-by-side: PDF + consolidated rate card */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.agreement.filename || 'Source agreement'}</Typography>
            </Stack>
            {(() => {
              const src = s.agreement.pdfDataUrl || s.agreement.s3Url;
              return src
                ? <Box component="embed" src={`${src}#toolbar=0&navpanes=0`} type="application/pdf" sx={{ width: '100%', height: 420, display: 'block', border: 0 }} />
                : <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="caption" sx={{ color: 'text.secondary' }}>PDF not available — upload the signed agreement on the Commercials step.</Typography></Box>;
            })()}
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionTitle icon="📊" title="Generated consolidated rate card" />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ my: 1 }}>
            {accs.map((a) => <Chip key={a} label={a} size="small" variant={a === previewAcc ? 'filled' : 'outlined'} color={a === previewAcc ? 'primary' : 'default'} onClick={() => setPreviewAcc(a)} sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }} />)}
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'background.neutral', fontWeight: 700, fontSize: 10 } }}>
                  <TableCell>Zone</TableCell><TableCell align="right">Slab</TableCell><TableCell align="right">Price</TableCell><TableCell align="right">RVP nQC</TableCell><TableCell align="right">RVP QC</TableCell><TableCell align="right">RTO%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.zone}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{r.start}–{r.max}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>₹{r.price}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>₹{r.rvp_noqc}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>₹{r.rvp_qc}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{r.rto_pct}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>Showing first {previewRows.length} rows of {rows.filter((r) => r.account_code === previewAcc).length} for {previewAcc}.</Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

export function validateReview(wizard) {
  if (wizard.effective_mode === 'from_date' && !wizard.effective_date) return { eff: 'Pick a go-live date (tomorrow onwards) or choose Immediate.' };
  if (wizard.effective_mode === 'from_date' && wizard.effective_date <= new Date().toISOString().slice(0, 10)) return { eff: 'Go-live date must be tomorrow or later.' };
  return {};
}
